import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Search, Plus, X, Download, Trash2, RotateCcw } from "lucide-react";
import { exportCSV } from "@/lib/exports";
import { useAuth } from "@/context/AuthContext";
import usePageTitle from "@/hooks/use-page-title";

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  whatsapp: "",
  gst: "",
  address: "",
  contact_person: "",
  notes: "",
};

export default function Customers() {
  usePageTitle("Customers");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [source, setSource] = useState("all"); // "all" | "online" | "offline"
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (showArchived) params.include_archived = true;
    // Always fetch all so we can show live counts in the tabs — filter client-side.
    return api.get("/customers", { params }).then((r) => setRows(r.data));
  };

  useEffect(() => {
    load();
     
  }, [q, showArchived]);

  // Derived stats + filtered rows
  const counts = {
    all: rows.length,
    online: rows.filter((c) => c.source === "online").length,
    offline: rows.filter((c) => c.source !== "online").length,
  };
  const visibleRows = rows.filter((c) => {
    if (source === "online") return c.source === "online";
    if (source === "offline") return c.source !== "online";
    return true;
  });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/customers", form);
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Archive customer "${c.name}"?\n\nTheir historical orders and invoices will be kept for GST/audit records, and their login will be disabled. You can restore them anytime from "Show archived".`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const hardDelete = async (c) => {
    if (!window.confirm(`PERMANENTLY DELETE "${c.name}"?\n\nThis will remove the customer AND all their orders, invoices, and payments forever. This cannot be undone.`)) return;
    if (!window.confirm(`Are you absolutely sure? Type OK on the next prompt.`)) return;
    const conf = window.prompt('Type "DELETE" to confirm permanent removal:');
    if (conf !== "DELETE") return;
    try {
      await api.delete(`/customers/${c.id}?force=true`);
      load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const restore = async (c) => {
    if (!window.confirm(`Restore customer "${c.name}"?\n\nTheir login will be re-enabled with the phone number as the default password.`)) return;
    try {
      await api.post(`/customers/${c.id}/restore`);
      load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-root">
      <div className="flex items-end justify-between border-b border-[hsl(var(--border))] pb-6">
        <div>
          <div className="overline mb-2">Directory</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
            Customers
          </h1>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn-swiss btn-accent"
          data-testid="new-customer-btn"
        >
          <Plus size={14} /> New Customer
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 max-w-md flex-1">
          <Search size={16} className="text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, company, phone, GST…"
            className="flex-1 bg-transparent px-3 py-2 text-sm outline-none"
            data-testid="customer-search-input"
          />
        </div>
        <button
          onClick={() => exportCSV("customers", visibleRows.map((c) => ({
            Name: c.name, Company: c.company, Phone: c.phone, Email: c.email,
            GST: c.gst, Address: c.address, Source: c.source, Outstanding: c.outstanding,
          })))}
          className="btn-swiss btn-outline"
          data-testid="customers-export-csv"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Source filter tabs */}
      <div className="flex items-center gap-1 border-b border-[hsl(var(--border))]" data-testid="customer-source-tabs">
        {[
          { key: "all", label: "All", accent: "#FF6B00" },
          { key: "online", label: "Online", accent: "#10b981" },
          { key: "offline", label: "Offline", accent: "#71717a" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setSource(t.key)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
              source === t.key
                ? "border-[#FF6B00] text-[#FF6B00]"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`customer-tab-${t.key}`}
          >
            {t.label}{" "}
            <span className="ml-1 text-[10px] font-mono opacity-70">({counts[t.key]})</span>
          </button>
        ))}
      </div>

      <div className="card-swiss overflow-hidden" data-testid="customers-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-4 py-3 text-left overline">Name</th>
              <th className="px-4 py-3 text-left overline">Company</th>
              <th className="px-4 py-3 text-left overline">Phone</th>
              <th className="px-4 py-3 text-left overline">Source</th>
              <th className="px-4 py-3 text-left overline">GST</th>
              <th className="px-4 py-3 text-right overline">Outstanding</th>
              {isAdmin && <th className="px-4 py-3 text-right overline">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((c) => (
              <tr
                key={c.id}
                className={`border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] ${c.is_deleted ? "opacity-60" : ""}`}
                data-testid={`customer-row-${c.id}`}
              >
                <td className="px-4 py-3 font-semibold">
                  <Link to={`/customers/${c.id}`} className="hover:text-[#FF6B00]">
                    {c.name}
                  </Link>
                  {c.is_deleted && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wider border border-[hsl(var(--border))] px-1.5 py-0.5 text-muted-foreground">
                      Archived
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">{c.company}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.phone}</td>
                <td className="px-4 py-3">
                  {c.source === "online" ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border border-emerald-300 bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full"
                      data-testid={`customer-source-badge-${c.id}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Online
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border border-zinc-200 bg-zinc-50 text-zinc-600 px-1.5 py-0.5 rounded-full"
                      data-testid={`customer-source-badge-${c.id}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                      Offline
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{c.gst || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">
                  ₹{Math.round(c.outstanding || 0).toLocaleString("en-IN")}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {c.is_deleted ? (
                      <>
                        <button
                          onClick={() => restore(c)}
                          className="p-1.5 hover:text-[#FF6B00]"
                          title="Restore customer"
                          data-testid={`restore-customer-${c.id}`}
                        >
                          <RotateCcw size={14} />
                        </button>
                        <button
                          onClick={() => hardDelete(c)}
                          className="p-1.5 hover:text-red-600 text-red-500"
                          title="Delete permanently"
                          data-testid={`hard-delete-customer-${c.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => remove(c)}
                        className="p-1.5 hover:text-[#FF6B00]"
                        title="Archive customer"
                        data-testid={`delete-customer-${c.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="px-4 py-12 text-center text-muted-foreground">
                  {rows.length === 0 ? "No customers yet." : `No ${source === "all" ? "" : source + " "}customers match.`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="customer-modal">
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] max-w-2xl w-full">
            <div className="flex items-center justify-between p-5 border-b border-[hsl(var(--border))]">
              <div className="font-display text-xl font-bold">New Customer</div>
              <button onClick={() => setShowForm(false)} data-testid="close-customer-modal">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["name", "Name *", true],
                ["company", "Company"],
                ["email", "Email"],
                ["phone", "Phone *", true],
                ["whatsapp", "WhatsApp"],
                ["gst", "GST Number"],
                ["contact_person", "Contact Person"],
              ].map(([k, label, req]) => (
                <div key={k}>
                  <label className="overline block mb-2">{label}</label>
                  <input
                    required={!!req}
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
                    data-testid={`customer-input-${k}`}
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="overline block mb-2">Address</label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  rows={2}
                  className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
                  data-testid="customer-input-address"
                />
              </div>
              {err && (
                <div className="md:col-span-2 text-sm text-red-600" data-testid="customer-form-error">
                  {err}
                </div>
              )}
              <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-swiss btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn-swiss btn-accent" data-testid="save-customer-btn">
                  Save Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
